import './segmentedToggle.scss';
import React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/component/ds/utils';

type SegmentedToggleGroupProps = Omit<
  Extract<React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root>, { type: 'single' }>,
  'type'
>;

const SegmentedToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  SegmentedToggleGroupProps
>(({ className, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    type='single'
    className={cn(
      'segmented-toggle ds-flex ds-w-fit ds-max-w-full ds-items-center ds-overflow-x-auto',
      className
    )}
    {...props}
  />
));
SegmentedToggleGroup.displayName = 'SegmentedToggleGroup';

const segmentedToggleItemVariants = cva(
  [
    'ds-inline-flex ds-shrink-0 ds-items-center ds-justify-center ds-gap-2',
    'ds-h-9 ds-py-[7.5px] ds-border ds-border-border',
    'ds-text-sm ds-font-medium ds-whitespace-nowrap ds-transition-colors',
    'ds-max-w-[200px] [&>span]:ds-truncate',
    /* Positioned so the focus ring paints over the neighbouring segment's
     * border rather than being clipped by it. */
    'ds-relative focus-visible:ds-z-10',
    'focus-visible:ds-outline-none focus-visible:ds-shadow-chemelex-focus',
    'disabled:ds-pointer-events-none disabled:ds-opacity-50',
    'ds-bg-background ds-text-foreground hover:ds-bg-muted'
  ],
  {
    variants: {
      // Collapse adjacent borders into one line rather than doubling them.
      left: {
        end: 'ds-rounded-l-full ds-pl-2.5',
        inner: 'ds-pl-2 -ds-ml-px'
      },
      right: {
        end: 'ds-rounded-r-full ds-pr-2.5',
        inner: 'ds-pr-2'
      }
    },
    defaultVariants: { left: 'inner', right: 'inner' }
  }
);

export interface SegmentedToggleItemProps
  extends React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item>,
  VariantProps<typeof segmentedToggleItemVariants> {}

const SegmentedToggleItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  SegmentedToggleItemProps
>(({ className, left, right, ...props }, ref) => (
  <ToggleGroupPrimitive.Item
    ref={ref}
    className={cn(
      segmentedToggleItemVariants({ left, right }),
      'data-[state=on]:ds-bg-tab-active data-[state=on]:ds-text-primary-foreground',
      className
    )}
    {...props}
  />
));
SegmentedToggleItem.displayName = 'SegmentedToggleItem';

export interface SegmentedToggleOption {
  value: string;
  label: string;
}

export interface SegmentedToggleProps {
  options: SegmentedToggleOption[];
  /** The selected option's value. */
  value: string;
  onChange: (value: string) => void;
  /** Names the group for screen readers; there is no visible label. */
  'aria-label': string;
  className?: string;
  testId?: string;
}

/*
 * Radix treats an empty item value as "nothing selected" (it builds the pressed
 * set with `value ? [value] : []`), but callers use '' as their "no filter"
 * sentinel. Swapping in a token across the primitive boundary keeps the empty
 * option selectable without leaking the token to callers.
 */
const ALL_VALUE = '__all__';

/**
 * A single-select segmented control.
 *
 * @param {SegmentedToggleProps} props see SegmentedToggleProps
 * @returns {JSX.Element} the button group
 */
export function SegmentedToggle ({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  className,
  testId
}: SegmentedToggleProps) {
  return (
    <SegmentedToggleGroup
      aria-label={ariaLabel}
      className={className}
      data-testid={testId}
      value={value === '' ? ALL_VALUE : value}
      /* Radix clears the value when the active item is pressed again. This is a
       * filter, not a toggle, so a press on the selection is a no-op. */
      onValueChange={(next) => {
        if (next === '') return;
        onChange(next === ALL_VALUE ? '' : next);
      }}
    >
      {options.map((option, index) => (
        <SegmentedToggleItem
          key={option.value}
          value={option.value === '' ? ALL_VALUE : option.value}
          left={index === 0 ? 'end' : 'inner'}
          right={index === options.length - 1 ? 'end' : 'inner'}
          data-testid={testId ? `${testId}-${option.value || 'all'}` : undefined}
          onMouseDown={(event) => {
            if (event.button === 0 && !event.ctrlKey && option.value !== value) {
              onChange(option.value);
            }
          }}
        >
          <span title={option.label}>{option.label}</span>
        </SegmentedToggleItem>
      ))}
    </SegmentedToggleGroup>
  );
}

export { SegmentedToggleGroup, SegmentedToggleItem, segmentedToggleItemVariants };
